import React, { useMemo, useState } from 'react';
import { Button, Input, Modal, Popover } from '@prio365/prio365-react-library';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { makePrioStyles } from '../../../theme/utils';
import { useSelector } from 'react-redux';
import { getUserMe, RootReducerState } from '../../../apps/main/rootReducer';
import { useTheme } from 'react-jss';
import { PrioTheme } from '../../../theme/types';
import classNames from 'classnames';
import { User } from '../../../models/User';
import DropDownButton from '@prio365/prio365-react-library/lib/Button/components/DropdownButton';

const useStyles = makePrioStyles((theme) => ({
  actionsBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.old.spacing.unit(2),
    backgroundColor: theme.old.palette.backgroundPalette.sub,
    borderBottom: `1px solid ${theme.old.palette.backgroundPalette.content}`,
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  actionsBarDefault: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    transition: 'opacity 0.3s ease, transform 0.3s ease',
  },

  actionsBarDefaultHidden: {
    opacity: 0,
    transform: 'translateY(-10px)',
    pointerEvents: 'none',
    position: 'absolute',
  },

  editActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    gap: 8,
    opacity: 1,
    transform: 'translateY(0)',
    transition: 'opacity 0.3s ease, transform 0.3s ease',
    position: 'absolute',
    paddingLeft: theme.old.spacing.unit(3),
  },

  editActionsHidden: {
    opacity: 0,
    transform: 'translateY(-10px)',
    pointerEvents: 'none',
  },

  infoIcon: {
    height: 20,
    minWidth: 20,
    marginTop: 8,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
}));

interface DashboardActionsBarProps {
  isEditMode: boolean;
  isLoading: boolean;
  onToggleEditMode: () => void;
  onSaveLayout: () => void;
  onDefaultGlobalDashboardSettingSave: () => void;
  onResetLayout: () => void;
  onCancelChanges?: () => void;
  hasChanges?: boolean;
  onOpenAddWidgetDrawer?: () => void;
  projectId?: string;
  dashboardName?: string | null;
  setDashboardName?: (name: string | null) => void;
  isProjectAdmin?: boolean;
}

type TimeOfDay = 'morning' | 'afternoon' | 'evening' | 'night';

const DashboardActionsBar: React.FC<DashboardActionsBarProps> = ({
  isEditMode,
  isLoading,
  onToggleEditMode,
  onSaveLayout,
  onDefaultGlobalDashboardSettingSave,
  onResetLayout,
  onCancelChanges,
  onOpenAddWidgetDrawer,
  hasChanges = false,
  projectId,
  dashboardName,
  setDashboardName,
  isProjectAdmin = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme<PrioTheme>();

  const userMe = useSelector<RootReducerState, User>(getUserMe);
  const userIsGlobalAdmin =
    userMe?.prioData?.globalRoles?.includes('globalAdmin') ?? false;
  const [cancelChangesModalVisible, setCancelChangesModalVisible] =
    useState(false);
  const [saveGloballyModalVisible, setSaveGloballyModalVisible] =
    useState(false);

  const [resetLayoutModalVisible, setResetLayoutModalVisible] = useState(false);

  const timeOfDay: TimeOfDay = useMemo(() => {
    const now = new Date();
    const hours = now.getHours();
    if (hours >= 5 && hours < 12) {
      return 'morning';
    } else if (hours >= 12 && hours < 18) {
      return 'afternoon';
    } else if (hours >= 18 && hours < 22) {
      return 'evening';
    } else {
      return 'night';
    }
  }, []);

  const user = useSelector(getUserMe);

  return (
    <div className={classes.actionsBar}>
      <div
        className={classNames(classes.actionsBarDefault, {
          [classes.editActionsHidden]: isEditMode,
        })}
      >
        {user && !projectId && (
          <h1 style={{ margin: 0, lineHeight: 0 }}>
            {t(`dashboard:widgets.welcome.${timeOfDay}`, {
              userName: user.givenName ?? user.displayName,
            })}
          </h1>
        )}
        {projectId && (
          <h1 style={{ margin: 0, lineHeight: 0, padding: '16px' }}>
            {dashboardName}
          </h1>
        )}
        {(isProjectAdmin || !projectId) && (
          <div>
            <Popover
              placement="bottom"
              content={t('dashboard:widgets.actionsBar.info')}
            >
              <Button
                className={classes.infoIcon}
                iconProp={['fal', 'info-circle']}
                iconColor={theme.colors.application.typography.muted}
                type="link"
              />
            </Popover>
            <Button type="link" onClick={onToggleEditMode}>
              <FontAwesomeIcon
                icon={['fal', 'pencil-alt']}
                style={{ marginRight: '4px' }}
              />
              {t('dashboard:widgets.actionsBar.editDashboard')}
            </Button>
          </div>
        )}
      </div>

      <div
        className={classNames(classes.editActions, {
          [classes.editActionsHidden]: !isEditMode,
        })}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {projectId && (
            <Input
              value={dashboardName}
              onChange={(value: string) => setDashboardName?.(value)}
              style={{ width: '200px', marginRight: '8px' }}
              disabled={isLoading}
              size="small"
            />
          )}

          <Button
            type="default"
            onClick={() => setResetLayoutModalVisible(true)}
            style={{ marginRight: '8px' }}
            disabled={isLoading}
          >
            <FontAwesomeIcon
              icon={['fal', 'undo']}
              style={{ marginRight: '4px' }}
            />
            {t('dashboard:widgets.actionsBar.resetDashboard')}
          </Button>
          <Button
            type="default"
            onClick={onOpenAddWidgetDrawer}
            style={{ marginRight: '8px' }}
            disabled={isLoading}
          >
            <FontAwesomeIcon
              icon={['fal', 'plus']}
              style={{ marginRight: '4px' }}
            />
            {t('dashboard:widgets.actionsBar.addWidget')}
          </Button>
        </div>
        <div>
          <Button
            type="default"
            onClick={() =>
              hasChanges
                ? setCancelChangesModalVisible(true)
                : onToggleEditMode()
            }
            disabled={isLoading}
            style={{ marginRight: '8px' }}
          >
            {t('dashboard:widgets.actionsBar.cancelChanges')}
          </Button>
          <DropDownButton
            type="primary"
            onClick={onSaveLayout}
            loading={isLoading}
            disabled={!hasChanges}
            dropdownOptions={
              userIsGlobalAdmin
                ? [
                    {
                      label: t('dashboard:widgets.actionsBar.saveGlobal'),
                      value: 'saveGlobally',
                      onClick: () => setSaveGloballyModalVisible(true),
                      iconProp: ['fal', 'globe'],
                    },
                  ]
                : undefined
            }
          >
            {t('dashboard:widgets.actionsBar.saveChanges')}
          </DropDownButton>
        </div>
      </div>

      <Modal
        title={t('dashboard:widgets.saveGloballyModal.title')}
        visible={saveGloballyModalVisible}
        onClose={() => {
          setSaveGloballyModalVisible(false);
        }}
        okText={t('dashboard:widgets.saveGloballyModal.okText')}
        cancelText={t('dashboard:widgets.saveGloballyModal.cancelText')}
        onOk={() => {
          onDefaultGlobalDashboardSettingSave();
          onSaveLayout();
          setSaveGloballyModalVisible(false);
        }}
      >
        {t('dashboard:widgets.saveGloballyModal.content')}
      </Modal>

      <Modal
        title={t('dashboard:widgets.cancelChangesModal.title')}
        visible={cancelChangesModalVisible}
        onClose={() => {
          setCancelChangesModalVisible(false);
        }}
        okText={t('dashboard:widgets.cancelChangesModal.okText')}
        cancelText={t('dashboard:widgets.cancelChangesModal.cancelText')}
        onOk={() => {
          onCancelChanges?.();
          setCancelChangesModalVisible(false);
        }}
      >
        {t('dashboard:widgets.cancelChangesModal.content')}
      </Modal>

      <Modal
        title={t('dashboard:widgets.resetLayoutModal.title')}
        visible={resetLayoutModalVisible}
        onClose={() => {
          setResetLayoutModalVisible(false);
        }}
        okText={t('dashboard:widgets.resetLayoutModal.okText')}
        cancelText={t('dashboard:widgets.resetLayoutModal.cancelText')}
        onOk={() => {
          onResetLayout();
          setResetLayoutModalVisible(false);
        }}
      >
        {t('dashboard:widgets.resetLayoutModal.content')}
      </Modal>
    </div>
  );
};

export default DashboardActionsBar;
